import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlexyForm, FormObject } from '@europrocurement/flexy-form';
import { Box, Typography } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import type {
    GenericModalMailType,
    DriveDocumentCategory,
    DriveDocumentStateType,
} from '@europrocurement/flexy-components/types/GenericModalMailType';
import { FlexyButton, useModal, useUploader } from '@europrocurement/flexy-components';
import { MailApiSendMailMailCollectionRequest } from '@europrocurement/l2d-domain/openApi/ApiMail';
import { useMailService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { markDownToHtml, plainTextToHtml } from '@europrocurement/l2d-utils';
import { faWandMagicSparkles } from '@fortawesome/sharp-regular-svg-icons';
import { IaButton } from '@europrocurement/l2d-modules/modules/IaServices';
import { type ContactItem } from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useExportDossier';
import DocumentsSelectorsForm from '@europrocurement/l2d-modules/modules/Dossiers/components/detail/DocumentsSelectorsForm/DocumentsSelectorsForm';
import UploadProgressSnackbar from '../../../Dossiers/components/shared/UploadProgressSnackbar/UploadProgressSnackbar';
import { useDriveDocumentsCtx } from '../../../../providers/DriveDocumentsProvider';
import useFormStructureGenericModal from '../../hooks/useFormStructureGenericModal';

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    msg: 'application/vnd.ms-outlook',
};

/**
 * GenericModalMailContent component
 *
 * A flexible email composition component that provides a form for composing and sending emails.
 * It includes support for:
 * - Dynamic field visibility (recipients, CC, BCC, subject, body, attachments)
 * - Built-in AI-powered text correction and improvement features
 * - Custom field ordering
 * - Form context access for parent components
 * - Markdown to HTML conversion for email body
 * - File attachments support
 *
 * @component
 * @example
 * // Basic usage with just recipients and body
 * <GenericModalMailContent
 *   to={[{ emailAddress: 'user@example.com', firstName: 'John', lastName: 'Doe' }]}
 *   body="Email content here"
 *   showBody={true}
 *   onSuccess={() => console.log('Email sent')}
 * />
 *
 * @example
 * // Advanced usage with all features
 * <GenericModalMailContent
 *   to={recipients}
 *   cc={ccList}
 *   cci={bccList}
 *   subject="Email Subject"
 *   body="Email body content"
 *   showTo={true}
 *   showCC={true}
 *   showCCI={true}
 *   showSubject={true}
 *   showBody={true}
 *   showAttachments={true}
 *   showIaButtons={true}
 *   iaChildren={<CustomIaButton />}
 *   searchParamsTiersIds={[1, 2, 3]}
 *   getFormContext={(ctx) => setFormContext(ctx)}
 *   fieldsOrdering={['recipients', 'subject', 'iaButtons', 'mailBody']}
 *   autoBalisesForMail={true}
 *   onSuccess={handleSuccess}
 * />
 */
const GenericModalMailContent: React.FunctionComponent<GenericModalMailType> = function ({
    to,
    cc = [],
    cci = [],
    subject,
    body,
    attachments,
    searchParamsTiersIds,
    showTo = true,
    showCC = true,
    showCCI = false,
    showSubject = true,
    showBody = true,
    showAttachments = true,
    additionnalReactNode,
    showIaButtons = true,
    iaChildren,
    SxProps,
    customButtonsFooter,
    fieldsOrdering,
    getFormContext,
    autoBalisesForMail = false,
    onSuccess,
    driveDocuments,
    driveDocumentCategories = [
        'factures',
        'devis',
        'encaissements',
        'docs_requis',
        'docs_remis',
        'annonces_legales',
        'docs_signes',
    ] as DriveDocumentCategory[],
    dossiersIds = [],
    collectionIds = [],
    showDriveDocButton = false,
    syncGreeting = false,
    showSignature = false,
}) {
    const { files, addFiles } = useUploader();
    const { ctx, loadCategories } = useDriveDocumentsCtx();
    const [showDriveSelector, setShowDriveSelector] = useState(false);
    const [selectedDriveDocs, setSelectedDriveDocs] = useState<DriveDocumentStateType[]>([]);
    const [driveDocs, setDriveDocs] = useState<DriveDocumentStateType[]>([]);
    const [preSelectedDriveDocsIds, setPreSelectedDriveDocsIds] = useState<number[]>([]);
    const [submittingForm, setSubmittingForm] = useState(false);

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { mailApi } = useMailService();
    const { modalActions } = useModal();
    const keycloak = UseKeycloakService();
    const senderEmail = useRef('');
    const senderName = useRef('');
    const uploadProgressIncrementRef = useRef<(() => void) | null>(null);

    const initialBody = (() => {
        const base = body || '';
        if (!showSignature || !base) return base;
        return `${base}\n\nCordialement,\n${keycloak.getName()}`;
    })();

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            mailBody: initialBody,
            subject: subject || '',
            recipients: to || [],
            recipientsCC: cc || [],
            recipientsCCI: cci || [],
        } as FormObject,
    });

    // To remove
    useEffect(() => {
        setPreSelectedDriveDocsIds([]);
    }, []);

    useEffect(() => {
        setDriveDocs(
            driveDocs.filter((dd) => files.some((f) => f.name === `${dd.label}.${dd.type}`)),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    // Build default IA Buttons with optional additional children
    const defaultIaButtons = useMemo(() => {
        if (!formContext || !showIaButtons || !showBody) {
            return null;
        }

        return (
            <>
                <IaButton
                    getParams={() => ({
                        textContent: formContext.getValues('mailBody'),
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            formContext.setValue('mailBody', correctedText);
                            enqueueSnackbar('Correction effectuée');
                        }
                    }}
                />
                <IaButton
                    applicationName="L2D_Resume"
                    label="Améliorer le texte"
                    icon={faWandMagicSparkles}
                    getParams={() => ({
                        textContent: formContext.getValues('mailBody'),
                    })}
                    onSuccess={(data) => {
                        const correctedText = data.answer;
                        if (correctedText) {
                            formContext.setValue('mailBody', correctedText);
                            enqueueSnackbar('Amélioration effectuée');
                        }
                    }}
                />
                {iaChildren}
            </>
        );
    }, [showIaButtons, showBody, iaChildren, formContext, enqueueSnackbar]);

    const handleDriveDocConfirm = useCallback(async () => {
        const docsToProcess = selectedDriveDocs.filter((doc) => doc.onGetBlob);

        setDriveDocs([...driveDocs, ...docsToProcess]);

        const progressKey = enqueueSnackbar(
            <UploadProgressSnackbar
                total={docsToProcess.length}
                onMount={(increment) => {
                    uploadProgressIncrementRef.current = increment;
                }}
            />,
            { variant: 'info', persist: true },
        );

        const TYPE_NO_TYPE = 66;
        const blobPromises = docsToProcess.map(async (doc) => {
            try {
                const blob = await doc.onGetBlob!();
                if (!blob) return null;
                const mimeType = MIME_TYPE_BY_EXTENSION[doc.type ?? ''] ?? blob.type;
                const rawName =
                    doc.documentTypeId === TYPE_NO_TYPE ? doc.fileName || doc.label : doc.label;
                const baseName = rawName || 'piece-jointe';
                const fileName =
                    doc.type && !baseName.includes('.') ? `${baseName}.${doc.type}` : baseName;
                return new File([blob], fileName, { type: mimeType });
            } catch (err) {
                console.error(err);
                enqueueSnackbar(
                    `Une erreur est survenue lors de l'ajout de "${doc.label ?? 'pièce jointe'}"`,
                    { variant: 'error' },
                );
                return null;
            } finally {
                uploadProgressIncrementRef.current?.();
            }
        });

        const files2 = (await Promise.all(blobPromises)).filter((f): f is File => f !== null);
        closeSnackbar(progressKey);
        addFiles(files2);
        setShowDriveSelector(false);
    }, [selectedDriveDocs, addFiles, enqueueSnackbar, closeSnackbar, driveDocs, setDriveDocs]);

    const { getFormStructure } = useFormStructureGenericModal({
        to,
        cc,
        cci,
        subject,
        body,
        attachments,
        searchParamsTiersIds,
        showTo,
        showCC,
        showCCI,
        showSubject,
        showBody,
        showIaButtons,
        iaChildren: defaultIaButtons,
        showAttachments,
        additionnalReactNode,
        SxProps,
        customButtonsFooter,
        fieldsOrdering,
        showDriveDocButton,
        onShowDriveDocs: showDriveDocButton ? () => setShowDriveSelector(true) : undefined,
    });
    type MailFormObject = {
        recipients: ContactItem[] | undefined;
        recipientsCC: ContactItem[] | undefined;
        recipientsCCI: ContactItem[] | undefined;
        subject: string | undefined;
        mailBody: string | undefined;
    };

    useEffect(() => {
        senderEmail.current = keycloak.getEmail();
        senderName.current = keycloak.getName();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = useCallback<SubmitHandler<MailFormObject>>(
        async (values) => {
            if (submittingForm) {
                return;
            }
            setSubmittingForm(true);
            let mailRecipientsTo = to.map((recipient) => recipient.emailAddress);
            if (values.recipients && values.recipients.length > 0) {
                mailRecipientsTo = values.recipients.map((recipient) => recipient.emailAddress);
            }

            // Is it a good idea to check recipientsCC length > 0 if trying to remove original pre values ??????
            let mailRecipientsCC = cc;
            if (values.recipientsCC && values.recipientsCC.length > 0) {
                mailRecipientsCC = values.recipientsCC.map((recipient) => recipient.emailAddress);
            }

            let mailRecipientsCCI = cci;
            if (values.recipientsCCI && values.recipientsCCI.length > 0) {
                mailRecipientsCCI = values.recipientsCCI.map((recipient) => recipient.emailAddress);
            }

            let mailSubject = subject;
            if (values.subject) {
                mailSubject = values.subject;
            }

            let mailBody = body;
            if (values.mailBody) {
                mailBody = values.mailBody as string;
            }
            if (autoBalisesForMail && mailBody) {
                mailBody = markDownToHtml({ content: mailBody });
            } else if (mailBody) {
                mailBody = plainTextToHtml(mailBody);
            }

            const request: MailApiSendMailMailCollectionRequest = {
                attachments2: files,
                details: {
                    from: senderEmail.current,
                    fromName: senderName.current,
                    tos: mailRecipientsTo,
                    ccs: mailRecipientsCC || null,
                    bccs: mailRecipientsCCI || null,
                    subject: mailSubject || null,
                    body: mailBody || null,
                    status: 'pending',
                    tags: ['B2D - Généric modal mail'],
                    replies: [senderEmail.current],
                    // @ts-expect-error : Let's ignore undefined property
                    useTemplating: true,
                    templateData: {
                        contentTemplate: 'empty-content',
                        mailContent: mailBody,
                        logo: {
                            alt: 'Legal2Digital Logo',
                            src: '/shared/images/LogoLegal2DigitalLight.png',
                            title: 'Legal2Digital',
                        },
                    },
                },
            };

            const emailPromise = await mailApi.sendMailMailCollection(request);
            setSubmittingForm(false);
            if (emailPromise.status === 201) {
                enqueueSnackbar('Le mail a bien été envoyé');
                if (onSuccess && typeof onSuccess !== 'undefined') {
                    onSuccess({
                        estimate: driveDocs.some((dd) => dd.category === 'devis'),
                    });
                } else {
                    modalActions.close();
                }
            } else {
                enqueueSnackbar("Une erreur est survenue lors de l'enovi du mail", {
                    variant: 'error',
                });
                modalActions.close();
            }
        },
        [
            files,
            body,
            cc,
            cci,
            subject,
            to,
            mailApi,
            enqueueSnackbar,
            modalActions,
            autoBalisesForMail,
            submittingForm,
            onSuccess,
            driveDocs,
        ],
    );

    /**
     * Prevent form submission when Enter key is pressed
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter' && e.ctrlKey === false) {
            e.preventDefault();
        }
    }, []);

    const formProps = {
        formContext,
        formObject: {},
        onkeydown: handleKeyDown,
        onSubmit: handleSubmit,
        gridProps: { sx: { gap: '8px' } },
        submitButton: {
            label: 'Envoyer',
            displayed: false,
        },
        isSubmitting: submittingForm,
    };

    const watchedRecipients = formContext.watch('recipients') as ContactItem[];

    useEffect(() => {
        if (!syncGreeting || !watchedRecipients) return;
        const currentBody = (formContext.getValues('mailBody') as string) || '';
        const firstBreakIndex = currentBody.indexOf('\n\n');
        const bodyWithoutGreeting =
            firstBreakIndex >= 0 ? currentBody.slice(firstBreakIndex) : '\n\n';
        const greeting =
            watchedRecipients.length === 1
                ? `Bonjour ${watchedRecipients[0].firstName} ${watchedRecipients[0].lastName},`
                : 'Bonjour,';
        formContext.setValue('mailBody', `${greeting}${bodyWithoutGreeting}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedRecipients, syncGreeting]);

    useEffect(() => {
        if (typeof getFormContext === 'function') {
            getFormContext({
                getMailBody: () => formContext.getValues('mailBody') as string | undefined,
                setMailBody: (value: string) => formContext.setValue('mailBody', value),
            });
        }
    }, [getFormContext, formContext]);

    useEffect(() => {
        if (ctx) {
            loadCategories({ dossiersIds, collectionIds, categories: driveDocumentCategories });
        }
    }, [dossiersIds, collectionIds, ctx, loadCategories, driveDocumentCategories]);

    const effectiveDriveDocuments = useMemo<DriveDocumentStateType[]>(() => {
        if (ctx) {
            return driveDocumentCategories.flatMap((cat) => ctx[cat].data);
        }
        return (driveDocuments as DriveDocumentStateType[]) ?? [];
    }, [ctx, driveDocumentCategories, driveDocuments]);

    const CATEGORIES_UNIQUE_BY_ID: DriveDocumentCategory[] = ['docs_requis', 'docs_remis'];

    const driveDocGroupByCategory = effectiveDriveDocuments.reduce<
        Record<string, DriveDocumentStateType[]>
    >((acc, doc) => {
        const key = doc.category ?? 'other';
        if (!acc[key]) acc[key] = [];
        // if 'docs_requis', 'docs_remis' we use the id instead of documentTypeId as the selector form select doscDoc by DocumentType
        const mappedDoc = CATEGORIES_UNIQUE_BY_ID.includes(key as DriveDocumentCategory)
            ? {
                  ...doc,
                  documentTypeId: doc.id,
                  label: !doc.hasType && doc.fileName ? doc.fileName : doc.label,
              }
            : doc;
        acc[key].push(mappedDoc);
        return acc;
    }, {});

    const CATEGORY_LABELS: Record<string, string> = {
        factures: 'Factures',
        devis: 'Devis',
        encaissements: 'Encaissements',
        docs_requis: 'Documents requis',
        docs_remis: 'Documents remis',
        annonces_legales: 'Annonces légales',
        docs_signes: 'Signature électronique',
    };

    return (
        <Box sx={{ minWidth: '20rem', maxWidth: '40rem', ...SxProps }}>
            {showDriveSelector ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {driveDocGroupByCategory &&
                        Object.entries(driveDocGroupByCategory).map(([category, docs]) => (
                            <Box key={category}>
                                <Typography
                                    variant="l2dsubtitles"
                                    sx={{ marginBottom: '8px', display: 'block' }}
                                >
                                    {CATEGORY_LABELS[category] ?? category}
                                </Typography>
                                <DocumentsSelectorsForm
                                    documents={docs}
                                    preSelectedDocumentsIds={preSelectedDriveDocsIds}
                                    setSelectedDocuments={setSelectedDriveDocs}
                                />
                            </Box>
                        ))}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <FlexyButton
                            variant="outlined"
                            onClick={() => setShowDriveSelector(false)}
                            label="Retour"
                        />
                        <FlexyButton
                            onClick={handleDriveDocConfirm}
                            label="Valider"
                        />
                    </Box>
                </Box>
            ) : (
                <FlexyForm
                    formStructure={getFormStructure()}
                    {...formProps}
                />
            )}
        </Box>
    );
};

export default GenericModalMailContent;
